import React, { useState, useEffect, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import { Utilities } from '../utils/Utilities';
import { YoutubeManager } from '../utils/YoutubeManager';
import type { YouTubeVideoItem } from '../types/youtube';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

interface SliderProps {
    /** Number of latest videos to load and display as slides (default: 5) */
    count?: number;
}

function formatVideoDate(isoDate: string | undefined): string {
    if (!isoDate) return '';
    return Utilities.formatDate(isoDate);
}

function Slider({ count = 5 }: SliderProps) {
    const swiperRef = useRef<SwiperType | undefined>(undefined);
    const [videos, setVideos] = useState<YouTubeVideoItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const loadVideos = async () => {
            try {
                const items = await YoutubeManager.getInstance().fetchYouTubeVideos(count);
                setVideos(items);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'An error occurred');
            } finally {
                setLoading(false);
            }
        };
        loadVideos();
    }, [count]);

    if (loading) {
        return (
            <section id="slider" className="slider section dark-background">
                <div className="container">
                    <div className="slider-loading">Loading...</div>
                </div>
            </section>
        );
    }

    if (error || videos.length === 0) {
        return (
            <section id="slider" className="slider section dark-background">
                <div className="container">
                    <div className="slider-error">
                        {error ? `Error: ${error}` : 'No videos available.'}
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section id="slider" className="slider section dark-background">
            <div className="container">
                <div className="swiper init-swiper">
                    <Swiper
                        onSwiper={(swiper: SwiperType) => {
                            swiperRef.current = swiper;
                        }}
                        loop={true}
                        speed={600}
                        autoplay={{
                            delay: 5000
                        }}
                        slidesPerView="auto"
                        centeredSlides={true}
                        modules={[Navigation, Pagination, Autoplay]}
                        navigation={{
                            nextEl: '.swiper-button-next',
                            prevEl: '.swiper-button-prev'
                        }}
                        pagination={{
                            el: '.swiper-pagination',
                            type: 'bullets',
                            clickable: true
                        }}
                        className="swiper-wrapper-container"
                    >
                        {videos.map((video) => (
                            <SwiperSlide key={video.videoId} className="slider-video-slide">
                                <a
                                    href={`https://www.youtube.com/watch?v=${video.videoId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="slide-thumbnail"
                                >
                                    <img
                                        src={video.thumbnail}
                                        alt={Utilities.decodeHtmlEntities(video.title)}
                                        className="slide-thumbnail-img"
                                    />
                                </a>
                                <div className="slide-content">
                                    <h2>
                                        <a
                                            href={`https://www.youtube.com/watch?v=${video.videoId}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {Utilities.decodeHtmlEntities(video.title)}
                                        </a>
                                    </h2>
                                    <div className="slide-meta">
                                        {video.publishedAt && (
                                            <span className="slide-meta-date">
                                                {formatVideoDate(video.publishedAt)}
                                            </span>
                                        )}
                                        {video.channelTitle && (
                                            <>
                                                {video.publishedAt && <span className="slide-meta-sep"> · </span>}
                                                <span className="slide-meta-channel">Channel: {Utilities.decodeHtmlEntities(video.channelTitle)}</span>
                                            </>
                                        )}
                                    </div>
                                    {video.description && (
                                        <p className="slide-description">{video.description}</p>
                                    )}
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>

                    <div className="swiper-button-next"></div>
                    <div className="swiper-button-prev"></div>
                    <div className="swiper-pagination"></div>
                </div>
            </div>
        </section>
    );
}

export default Slider;
